import React, { useState, useEffect } from 'react';

const DesktopBackground: React.FC = () => {
  const [currentBackground, setCurrentBackground] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchCurrentBackground();
  }, []);

  const fetchCurrentBackground = async () => {
    try {
      const response = await fetch('/api/desktop/background');
      const data = await response.json();
      setCurrentBackground(data.background);
    } catch (err) {
      console.error('Failed to fetch background:', err);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError('');
    setSuccess('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('background', file);

      const response = await fetch('/api/desktop/background', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      setSuccess('Background uploaded successfully! Refresh the desktop to see changes.');
      fetchCurrentBackground();
    } catch (err) {
      setError('Failed to upload background. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to remove the desktop background?')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/desktop/background', {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      setSuccess('Background removed successfully! Refresh the desktop to see changes.');
      setCurrentBackground(null);
    } catch (err) {
      setError('Failed to delete background. Please try again.');
    }
  };

  return (
    <div className="desktop-background-manager">
      <h3>Desktop Background</h3>

      {error && (
        <div style={{
          background: '#ff0000',
          color: '#fff',
          padding: '8px',
          marginBottom: '12px',
          fontSize: '11px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          background: '#008000',
          color: '#fff',
          padding: '8px',
          marginBottom: '12px',
          fontSize: '11px'
        }}>
          {success}
        </div>
      )}

      <div className="window" style={{ padding: '15px', marginBottom: '20px' }}>
        <h4>Current Background</h4>
        {currentBackground ? (
          <div>
            <div style={{
              width: '300px',
              height: '200px',
              border: '2px solid #808080',
              marginBottom: '12px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <img
                src={currentBackground}
                alt="Desktop background"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <button onClick={handleDelete}>Remove Background</button>
          </div>
        ) : (
          <p style={{ color: '#666', fontSize: '11px' }}>No background image set. Using default teal color.</p>
        )}
      </div>

      <div className="window" style={{ padding: '15px' }}>
        <h4>Upload New Background</h4>
        <p style={{ fontSize: '11px', marginBottom: '12px' }}>
          Upload an image to use as the desktop background. The image will be automatically scaled to fit the screen.
        </p>

        <div className="field-row-stacked">
          <label htmlFor="background-upload">Choose Image (Max 10MB)</label>
          <input
            id="background-upload"
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={uploading}
            style={{ marginTop: '4px' }}
          />
        </div>

        {uploading && (
          <div style={{ marginTop: '12px', fontSize: '11px' }}>
            Uploading...
          </div>
        )}
      </div>

      <div style={{
        marginTop: '20px',
        padding: '12px',
        background: '#ffffe1',
        border: '1px solid #808080',
        fontSize: '11px'
      }}>
        <strong>Note:</strong> After uploading or removing a background, refresh the desktop page to see the changes.
      </div>
    </div>
  );
};

export default DesktopBackground;
